import { makeStyles } from '@material-ui/styles'
import { listTenants } from 'account/components/userManagement/tenants/new-actions'
import { tenantsSelector } from 'account/components/userManagement/tenants/selectors'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import ListTableField from 'core/components/validatedForm/ListTableField'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import Theme from 'core/themes/model'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

const useStyles = makeStyles<Theme>((theme) => ({
  withUnits: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  unitsDropdown: {
    position: 'relative',
    bottom: 1,
  },
}))

const tenantColumns = [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'description',
    label: 'Description',
  },
]

interface Props {
  onSubmit: (body: any) => void
  volumeBackendName: string
  onClose: () => void
}

export default function AddNewVolumeType({ onSubmit, volumeBackendName, onClose }: Props) {
  const [errorMessage, setErrorMessage] = useState<{ title: string; message: string } | undefined>(
    undefined,
  )
  const defaultParams = {
    name: '',
    description: '',
    volumeBackend: '',
    metadata: [],
    tenants: [],
    public: true,
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)

  const { loading: loadingTenants } = useListAction(listTenants)
  const tenantsList = useSelector(tenantsSelector)

  // If the volume backend name is provided in the URL, set it in the form
  useEffect(() => {
    if (volumeBackendName) {
      updateParams({ volumeBackend: volumeBackendName })
    }
  }, [volumeBackendName])

  const submitForm = useCallback(async () => {
    if (params.metadata.some((data) => data.key === 'volume_backend_name')) {
      setErrorMessage({
        title: 'Invalid Key-Value Pair',
        message:
          'Cannot add volume_backend_name in Metadata Key-Value pair. Please select the Volume Backend from the dropdown.',
      })
      return
    }
    setErrorMessage(undefined)

    const inititalValue = !!params.volumeBackend
      ? { volume_backend_name: params.volumeBackend }
      : {}

    const metadata = params.metadata.reduce((accum, pair) => {
      return {
        ...accum,
        [pair.key]: pair.value,
      }
    }, inititalValue)

    const body = {
      name: params.name,
      description: params.description,
      extra_specs: metadata,
      'os-volume-type-access:is_public': params.public,
      tenants: params.tenants,
    }

    onSubmit(body)
  }, [params, onSubmit])

  const handleClose = () => {
    setParams(defaultParams)
    onClose()
  }

  return (
    <ModalForm
      open
      title={`Create New Volume Type`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitTitle={`Create Volume Type`}
      maxWidth={600}
      error={errorMessage}
    >
      <FormFieldSection title="Volume Type Configuration" alignContentLeft>
        <TextField
          id="name"
          label="Name"
          onChange={getParamsUpdater('name')}
          value={params.name}
          required
        />
        <TextField
          id="description"
          label="Description"
          onChange={getParamsUpdater('description')}
          value={params.description}
          required
        />
        <TextField
          id="volumeType"
          value={params.volumeBackend}
          label="Volume Backend"
          readOnly
          required
        />
        <KeyValuesField
          id="metadata"
          label="Metadata"
          value={params.metadata}
          onChange={getParamsUpdater('metadata')}
          addLabel="Add Metadata"
          allowMultipleValues
        />
      </FormFieldSection>
      <FormFieldSection title="Tenant Access" alignContentLeft>
        <CheckboxField
          id="public"
          label="Make Public"
          value={params.public}
          onChange={getParamsUpdater('public')}
          info="If checked, volume type will be available for every tenant."
        />
        {!params.public && (
          <>
            <Text variant="body2">
              Select the tenants that should have access to this volume type.
            </Text>
            <ListTableField
              id="tenants"
              data={tenantsList}
              loading={loadingTenants}
              columns={tenantColumns}
              onChange={getParamsUpdater('tenants')}
              value={params.tenants}
              uniqueIdentifier="id"
              searchTargets={['name']}
              multiSelection
            />
          </>
        )}
      </FormFieldSection>
    </ModalForm>
  )
}
